import React, { useState } from 'react';
import { Select, Button } from '@bufferapp/ui';
import { useAppDispatch, useAppSelector } from '~/store';
import type { RootState } from '~/store';
import type { Campaign } from '~/campaign/types';
import styled from 'styled-components';
import { red, redDark } from '@bufferapp/ui/style/colors';
import { assignCampaignToPost } from '~/post/thunks';
import { selectPostById } from '~/post/slice';
import { PostEntity } from '~/post/PostEntity';
import { CampaignItemLabel } from './CampaignLabel';

type PropType = {
  postId: string;
  size?: 'small' | 'medium';
};

type CampaignItem = {
  id: string;
  title: JSX.Element;
  selectedItemClick: () => void;
};

const RemoveCampaignButton = styled(Button)`
  color: ${red};
  margin-left: 8px;

  :hover {
    color: ${redDark};
  }
`;

export const PostCampaignSelector = ({
  postId,
  size = 'medium',
}: PropType): JSX.Element | null => {
  const [isAttachingToCampaign, setIsAttachingToCampaign] = useState(false);
  const [isDetachingFromCampaign, setIsDetachingFromCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  const dispatch = useAppDispatch();
  const campaigns = useAppSelector(
    (state: RootState) => state.campaignsList?.campaigns || []
  );
  const post = useAppSelector((state: RootState) =>
    selectPostById(state, postId)
  );
  const hasCampaign = PostEntity.hasCampaign(post);
  const campaignItems = [...campaigns];

  const getSelectedCampaign = (campaignId) => {
    return campaignItems.find((campaign) => campaign.id === campaignId);
  };

  const onSelectCampaign = async (campaignItem: CampaignItem) => {
    const campaign = getSelectedCampaign(campaignItem.id);
    setSelectedCampaign(campaign);
    if (campaign.id) {
      setIsAttachingToCampaign(true);
      await dispatch(assignCampaignToPost({ postId, campaignId: campaign.id }));
      setIsAttachingToCampaign(false);
    }
  };

  const onRemoveCampaign = async () => {
    setSelectedCampaign(null);
    setIsDetachingFromCampaign(true);
    await dispatch(assignCampaignToPost({ postId, campaignId: null }));
    setIsDetachingFromCampaign(false);
  };

  const getCampaignItems = () => {
    return campaignItems.map((campaign) => ({
      id: campaign.id,
      title: (
        <CampaignItemLabel
          name={campaign.name}
          isSelected={selectedCampaign?.id === campaign.id}
          color={campaign.color}
        />
      ),
      selectedItemClick: onSelectCampaign,
    }));
  };

  return (
    <>
      {hasCampaign ? (
        <RemoveCampaignButton
          type="text"
          size={size}
          label={
            isDetachingFromCampaign ? 'Removing Campaign' : 'Remove Campaign'
          }
          onClick={onRemoveCampaign}
        />
      ) : (
        <Select
          size={size}
          label={
            !isAttachingToCampaign
              ? selectedCampaign?.name || 'Add Campaign'
              : 'Adding Campaign'
          }
          items={getCampaignItems()}
          disabled={campaigns.length < 1 || isAttachingToCampaign}
          hideSearch
          onSelectClick={onSelectCampaign}
        />
      )}
    </>
  );
};
